import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api.js';
import {
  Building2,
  Plus,
  X,
  Check,
  Mail,
  FileText,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import type { Lieferschein, Manufacturer } from '../types';
import { ManufacturerLegalFields } from '../components/products/ManufacturerLegalFields';
import { ManufacturerListRow } from '../components/products/ManufacturerListRow';
import { downloadPdf, extractApiError } from '../utils/pdfDownload';

const MAX_EMAILS = 20;

const emailsOf = (m: Manufacturer): string[] =>
  Array.isArray(m.lieferschein_emails) ? m.lieferschein_emails : [];

const Manufacturers: React.FC = () => {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [originInput, setOriginInput] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [legalNameInput, setLegalNameInput] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [directorsInput, setDirectorsInput] = useState('');
  const [taxInput, setTaxInput] = useState('');
  const [vatInput, setVatInput] = useState('');
  const [emailInputs, setEmailInputs] = useState<string[]>(['']);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [profileMfg, setProfileMfg] = useState<Manufacturer | null>(null);
  const [profileLs, setProfileLs] = useState<Lieferschein[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    fetchManufacturers();
  }, []);

  const fetchManufacturers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/manufacturers');
      setManufacturers(res.data);
    } catch {
      toast.error('Failed to load manufacturers');
    } finally {
      setLoading(false);
    }
  };

  const toggleReportLanguage = async (manufacturer: Manufacturer) => {
    const next: 'de' | 'en' = manufacturer.field_report_language === 'en' ? 'de' : 'en';
    const apply = (row: Manufacturer) =>
      row.id === manufacturer.id ? { ...row, field_report_language: next } : row;
    setManufacturers((prev) => prev.map(apply));
    setProfileMfg((current) => (current ? apply(current) : current));
    try {
      await api.put(`/manufacturers/${manufacturer.id}`, { field_report_language: next });
    } catch {
      toast.error('Failed to update report language');
      fetchManufacturers();
    }
  };

  const setLegal = (m: Manufacturer | null) => {
    setLegalNameInput(m?.legal_company_name || '');
    setAddressInput(m?.registered_address || '');
    setDirectorsInput(m?.managing_directors || '');
    setTaxInput(m?.tax_number || '');
    setVatInput(m?.vat_id || '');
  };
  const openCreate = () => {
    setEditingId(null);
    setNameInput('');
    setOriginInput('');
    setPromoInput('');
    setLegal(null);
    setEmailInputs(['']);
    setFormOpen(true);
  };

  const openEdit = (m: Manufacturer) => {
    setEditingId(m.id);
    setNameInput(m.name);
    setOriginInput(m.origin || '');
    setPromoInput(m.newcustomer_promo || '');
    setLegal(m);
    const existing = emailsOf(m);
    setEmailInputs(existing.length > 0 ? existing : ['']);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setNameInput('');
    setOriginInput('');
    setPromoInput('');
    setLegal(null);
    setEmailInputs(['']);
    setEditingId(null);
  };

  const setEmailAt = (index: number, value: string) => {
    setEmailInputs((prev) => prev.map((e, i) => (i === index ? value : e)));
  };

  const addEmailRow = () => {
    setEmailInputs((prev) => (prev.length >= MAX_EMAILS ? prev : [...prev, '']));
  };

  const removeEmailRow = (index: number) => {
    setEmailInputs((prev) => {
      if (prev.length <= 1) return [''];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameInput.trim()) return;
    setSaving(true);
    try {
      const lieferschein_emails = emailInputs.map((v) => v.trim()).filter(Boolean);
      const payload = {
        name: nameInput.trim(),
        lieferschein_emails,
        origin: originInput.trim() || null,
        newcustomer_promo: promoInput.trim() || null,
        legal_company_name: legalNameInput.trim() || null,
        registered_address: addressInput.trim() || null,
        managing_directors: directorsInput.trim() || null,
        tax_number: taxInput.trim() || null,
        vat_id: vatInput.trim() || null,
      };
      if (editingId) {
        await api.put(`/manufacturers/${editingId}`, payload);
        toast.success('Manufacturer updated');
      } else {
        await api.post('/manufacturers', payload);
        toast.success('Manufacturer created');
      }
      closeForm();
      fetchManufacturers();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to save manufacturer';
      toast.error(typeof msg === 'string' ? msg : msg.message || 'Failed to save manufacturer');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/manufacturers/${deleteTargetId}`);
      toast.success('Manufacturer deleted');
      fetchManufacturers();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to delete manufacturer';
      toast.error(typeof msg === 'string' ? msg : msg.message || 'Failed to delete manufacturer');
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
    }
  };

  const openProfile = async (m: Manufacturer) => {
    setProfileMfg(m);
    setProfileLoading(true);
    setProfileLs([]);
    try {
      const res = await api.get(`/lieferscheine/by-manufacturer/${m.id}`);
      setProfileLs(res.data);
    } catch {
      toast.error('Failed to load delivery notes');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileDownload = async (ls: Lieferschein) => {
    try {
      await downloadPdf(
        `/lieferscheine/${ls.id}/pdf`,
        `${ls.lieferschein_number}.pdf`
      );
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Download failed: ${message}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-brand-100 rounded-lg p-2">
            <Building2 className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Manufacturers</h1>
            <p className="text-sm text-gray-500">{manufacturers.length} total</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus size={16} />
          Add Manufacturer
        </button>
      </div>

      {formOpen && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">
              {editingId ? 'Edit Manufacturer' : 'New Manufacturer'}
            </h2>
            <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Manufacturer name"
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Origin
                </label>
                <input
                  type="text"
                  value={originInput}
                  onChange={(e) => setOriginInput(e.target.value)}
                  placeholder="e.g. Kopenhagen"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  New-customer offer
                </label>
                <input
                  type="text"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  placeholder="e.g. 3+1 auf Noda"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <p className="sm:col-span-2 text-xs text-gray-400">
                Both appear in the follow-up email after a visit.
              </p>
              <ManufacturerLegalFields
                legalName={legalNameInput}
                address={addressInput}
                directors={directorsInput}
                taxNumber={taxInput}
                vatId={vatInput}
                onLegalName={setLegalNameInput}
                onAddress={setAddressInput}
                onDirectors={setDirectorsInput}
                onTaxNumber={setTaxInput}
                onVatId={setVatInput}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                Delivery note emails
              </label>
              <p className="text-xs text-gray-400 mt-1 mb-2">
                First address is To; additional addresses are CC when sending extracts.
              </p>
              <div className="space-y-2">
                {emailInputs.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                          {index === 0 ? 'To' : 'CC'}
                        </span>
                      </div>
                      <input
                        type="email"
                        value={value}
                        onChange={(e) => setEmailAt(index, e.target.value)}
                        placeholder={index === 0 ? 'lieferschein@brand.de' : 'cc@brand.de'}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeEmailRow(index)}
                      disabled={emailInputs.length === 1 && !value}
                      className="mt-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              {emailInputs.length < MAX_EMAILS && (
                <button
                  type="button"
                  onClick={addEmailRow}
                  className="mt-2 flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  <Plus size={14} />
                  Add email
                </button>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving || !nameInput.trim()}
                className="flex items-center gap-1.5 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                <Check size={15} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500 text-sm">Loading...</div>
      ) : manufacturers.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Building2 size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No manufacturers yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
          {manufacturers.map((m) => (
            <ManufacturerListRow
              key={m.id}
              manufacturer={m}
              onOpen={openProfile}
              onEdit={openEdit}
              onDelete={confirmDelete}
              onToggleLanguage={toggleReportLanguage}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeleteTargetId(null);
        }}
        title="Delete Manufacturer"
        message="Delete this manufacturer? This cannot be undone."
        confirmText="Delete"
        onConfirm={handleDelete}
        variant="danger"
      />

      {profileMfg && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-2xl rounded-t-[2rem] md:rounded-[2rem] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="p-5 md:p-6 overflow-y-auto flex-1">
              <div className="flex justify-between items-start mb-4">
                <div className="min-w-0 pr-4">
                  <h2 className="text-lg font-black text-gray-900">{profileMfg.name}</h2>
                  {emailsOf(profileMfg).length > 0 ? (
                    <ul className="mt-1 space-y-0.5">
                      {emailsOf(profileMfg).map((email, i) => (
                        <li key={email} className="text-xs text-gray-500 flex items-center gap-1">
                          <Mail size={11} />
                          <span className="font-semibold text-gray-400 w-6 shrink-0">
                            {i === 0 ? 'To' : 'CC'}
                          </span>
                          <span className="truncate">{email}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle size={11} />
                      No email configured
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setProfileMfg(null);
                    setProfileLs([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 font-black text-sm"
                >
                  CLOSE
                </button>
              </div>

              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                Delivery notes
              </h3>
              {profileLoading ? (
                <div className="text-center py-8 text-sm text-gray-400">Loading...</div>
              ) : profileLs.length === 0 ? (
                <div className="text-center py-8 text-xs text-gray-400">
                  <FileText size={32} className="mx-auto mb-2 opacity-40" />
                  No delivery notes for this manufacturer yet
                </div>
              ) : (
                <div className="space-y-2">
                  {profileLs.map((ls) => (
                    <div
                      key={ls.id}
                      className="flex items-center justify-between p-3 border-2 border-gray-100 rounded-xl"
                    >
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-gray-900">
                          {ls.lieferschein_number}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {ls.pos_name || 'No PoS'}{' '}
                          {ls.pos_city ? `- ${ls.pos_city}` : ''} ·{' '}
                          {new Date(ls.delivery_date).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleProfileDownload(ls)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white rounded-lg text-xs font-bold hover:bg-brand-700 transition-colors"
                      >
                        <Download size={12} />
                        PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manufacturers;
